<template>
  <b-modal
    v-model="modal"
    size="lg"
    dialog-class="rounded-[10px]"
    body-class="!px-[32px] !py-[3rem]"
    content-class="overflow-visible dns-record-modal-content"
    hide-header
    hide-footer
    centered
    lazy
  >
    <b-form
      class="flex flex-col"
      @submit.prevent="submit"
    >
      <h2 class="text-xl text-center mb-8">
        {{ form.id || form.solid ? 'Edit' : 'Add' }} DNS Record
      </h2>

      <b-form-group label="Hostname">
        <b-input-group
          :append="`.${domain}`"
          class="input-hostname"
        >
          <b-form-input
            v-model="form.name"
            placeholder="leave empty for root"
            :disabled="!!form.id"
            class="!h-[40px]"
          />
        </b-input-group>
      </b-form-group>

      <div class="w-full flex gap-[10px] mt-[12px]">
        <b-form-group
          label="Type"
          class="w-full"
        >
          <v-select
            v-model="form.type"
            :filterable="false"
            :options="aws ? awsTypes : typeOptions"
            :disabled="!!form.id"
            drop-down-classes="dns-record-modal-dropdown"
            class="custom-select"
            :style="{height: '40px !important'}"
            label="name"
            :reduce="item => item.value"
            @option:selected="() => form.value = ''"
          />
        </b-form-group>
        <b-form-group
          v-if="form.type !== 5"
          label="TTL"
          class="w-full"
        >
          <v-select
            v-model="form.ttl"
            :filterable="false"
            :options="ttlOptions"
            drop-down-classes="dns-record-modal-dropdown"
            class="custom-select"
            :style="{height: '40px !important'}"
            label="name"
            :reduce="item => item.value"
          />
        </b-form-group>
      </div>

      <div class="w-full flex gap-[10px] mt-[12px]">
        <b-form-group
          :label="typeOptions[valueField].field.name"
          class="w-full"
        >
          <b-textarea
            v-if="form.type === 3"
            v-model="form.value"
            :placeholder="typeOptions[valueField].field.placeholder"
            rows="5"
            required
            class="w-full"
          />
          <b-input
            v-else
            v-model="form.value"
            :placeholder="typeOptions[valueField].field.placeholder"
            required
            class="w-full"
          />
        </b-form-group>

        <b-form-group
          v-if="form.type === 4 || form.type === 'MX'"
          label="Priority"
          class="w-[220px]"
        >
          <b-input
            v-model="form.priority"
            placeholder="0 - 65535"
            type="number"
            :min="0"
            :max="65535"
          />
        </b-form-group>
      </div>

      <div
        v-if="form.type === 9"
        class="w-full flex gap-x-[10px]"
      >
        <b-form-group
          label="Flags"
          class="w-full"
        >
          <b-input
            v-model="form.flags"
            placeholder="0"
            type="number"
            :min="0"
            :max="65535"
            required
          />
        </b-form-group>

        <b-form-group
          label="Tag"
          class="w-full"
        >
          <b-input
            v-model="form.tag"
            placeholder="Issue"
            required
          />
        </b-form-group>
      </div>

      <div
        v-if="form.type === 8 || form.type === 'SRV'"
        class="w-full flex gap-x-[10px]"
      >
        <b-form-group
          label="Priority"
          class="w-full"
        >
          <b-input
            v-model="form.priority"
            placeholder="0 - 65535"
            type="number"
            :min="0"
            :max="65535"
            required
          />
        </b-form-group>

        <b-form-group
          label="Weight"
          class="w-full"
        >
          <b-input
            v-model="form.weight"
            placeholder="0 - 100"
            type="number"
            :min="0"
            :max="100"
            required
          />
        </b-form-group>

        <b-form-group
          label="Port"
          class="w-full"
        >
          <b-input
            v-model="form.port"
            placeholder="0 - 65535"
            type="number"
            :min="0"
            :max="65535"
            required
          />
        </b-form-group>
      </div>

      <div
        v-if="!aws && [0, 1, 2].includes(form.type)"
        class="w-full"
      >
        <b-button
          variant="text"
          class="p-0"
          @click="() => showAdvanced = !showAdvanced"
        >
          Advanced Settings
          <i
            class="fa fa-chevron-down text-xs ml-1 transition-transform"
            :class="{'rotate-180': showAdvanced}"
          />
        </b-button>
        <b-collapse
          v-model="showAdvanced"
          visible
          class="visible mt-[20px]"
        >
          <div class="w-full flex gap-x-[10px]">
            <b-form-group
              label="Routing Weight"
              class="w-full"
            >
              <b-input
                v-model="form.weight"
                type="number"
                required
              />
            </b-form-group>

            <b-form-group
              label="Monitoring"
              class="w-full"
            >
              <v-select
                v-model="form.monitor_type"
                :filterable="false"
                :options="monitorTypeOptions"
                drop-down-classes="dns-record-modal-dropdown"
                class="custom-select"
                :style="{height: '40px !important'}"
                label="name"
                :reduce="item => item.value"
              />
            </b-form-group>
          </div>

          <b-form-group
            label="Smart Record Type"
            class="w-1/2"
          >
            <v-select
              v-model="form.smart_routing_type"
              :filterable="false"
              :options="smartRoutingTypeOptions"
              drop-down-classes="dns-record-modal-dropdown"
              class="custom-select"
              :style="{height: '40px !important'}"
              label="name"
              :reduce="item => item.value"
            />
          </b-form-group>
        </b-collapse>
      </div>

      <template v-if="!aws && [0, 1, 2].includes(form.type)">
        <b-checkbox
          v-model="form.enable"
          switch
          size="lg"
        >
          Record Enabled
        </b-checkbox>

        <p class="text-base text-[#6E747A]">
          If disabled, the record will not respond to queries, but will show up in the panel. You can use this to temporarily disable records.
        </p>
      </template>

      <div class="inline-flex gap-x-[10px] mt-12">
        <b-btn
          variant="primary"
          type="submit"
          :disabled="loading"
        >
          {{ form.id || form.solid ? 'Edit Record' : 'Add Record' }}
          <b-spinner
            v-if="loading"
            class="ml-2"
            variant="white"
            small
          />
        </b-btn>
        <b-btn
          variant="secondary"
          type="button"
          :disabled="loading"
          @click="() => modal = false"
        >
          Cancel
        </b-btn>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
export default {
  name: 'DnsRecordModal',
  props: {
    value: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    form: {
      type: Object,
      default: () => {}
    },
    domain: {
      type: String,
      default: ''
    },
    aws: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showAdvanced: true,
      typeOptions: [
        {
          name: 'A',
          value: 0,
          field: {
            name: 'IPv4 Address',
            placeholder: 'Example: 192.0.2.55'
          }
        },
        {
          name: 'AAAA',
          value: 1,
          field: {
            name: 'IPv6 Address',
            placeholder: 'Example: 2001:0db8:85a3:0000:0000:8a2e:0370:7334'
          }
        },
        {
          name: 'CAA',
          value: 9,
          field: {
            name: 'Server Hostname',
            placeholder: 'caa.example.com'
          }
        },
        {
          name: 'CNAME',
          value: 2,
          field: {
            name: 'Target',
            placeholder: 'www.example.com'
          }
        },
        {
          name: 'MX',
          value: 4,
          field: {
            name: 'Mail Server',
            placeholder: 'mailserver.example.com'
          }
        },
        {
          name: 'NS',
          value: 12,
          field: {
            name: 'Target',
            placeholder: 'ns1.someserver.com'
          }
        },
        {
          name: 'PTR',
          value: 10,
          field: {
            name: 'Domain Name',
            placeholder: 'www.example.com'
          }
        },
        {
          name: 'RDR',
          value: 5,
          field: {
            name: 'URL',
            placeholder: 'https://mywebsite.com'
          }
        },
        {
          name: 'SRV',
          value: 8,
          field: {
            name: 'Server Hostname',
            placeholder: 'xmpp-server.example.com'
          }
        },
        {
          name: 'TXT',
          value: 3,
          field: {
            name: 'Content',
            placeholder: 'Example Text Entry'
          }
        }
        // {
        //   name: 'Flatten',
        //   value: 6
        // },
        // {
        //   name: 'PullZone',
        //   value: 7
        // },
        // {
        //   name: 'Script',
        //   value: 11
        // },
      ],
      awsTypes: [
        {
          name: 'A',
          value: 0,
          field: {
            name: 'IPv4 Address',
            placeholder: 'Example: 192.0.2.55'
          }
        },
        {
          name: 'AAAA',
          value: 1,
          field: {
            name: 'IPv6 Address',
            placeholder: 'Example: 2001:0db8:85a3:0000:0000:8a2e:0370:7334'
          }
        },
        {
          name: 'CNAME',
          value: 2,
          field: {
            name: 'Target',
            placeholder: 'www.example.com'
          }
        },
        {
          name: 'MX',
          value: 4,
          field: {
            name: 'Mail Server',
            placeholder: 'mailserver.example.com'
          }
        },
        {
          name: 'SRV',
          value: 8,
          field: {
            name: 'Server Hostname',
            placeholder: 'xmpp-server.example.com'
          }
        },
        {
          name: 'TXT',
          value: 3,
          field: {
            name: 'Content',
            placeholder: 'Example Text Entry'
          }
        }
      ],
      monitorTypeOptions: [
        {
          name: 'None',
          value: 0
        },
        {
          name: 'Ping',
          value: 1
        },
        {
          name: 'Http',
          value: 2
        }
      ],
      smartRoutingTypeOptions: [
        {
          name: 'None',
          value: 0
        },
        {
          name: 'Latency',
          value: 1
        },
        {
          name: 'Geolocation',
          value: 2
        }
      ],
      ttlOptions: [
        {
          name: '15s',
          value: 15
        },
        {
          name: '30s',
          value: 30
        },
        {
          name: '1m',
          value: 60
        },
        {
          name: '2m',
          value: 120
        },
        {
          name: '5m',
          value: 300
        },
        {
          name: '15m',
          value: 900
        },
        {
          name: '30m',
          value: 1800
        },
        {
          name: '1h',
          value: 3600
        },
        {
          name: '5h',
          value: 18000
        },
        {
          name: '12h',
          value: 43200
        },
        {
          name: '1d',
          value: 86400
        }
      ]
    }
  },
  computed: {
    modal: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    valueField () {
      if (this.aws) {
        const fieldType = typeof this.form.type === 'string'
        return this.typeOptions.findIndex(item => fieldType ? item.name : item.value === this.form.type)
      } else {
        return this.typeOptions.findIndex(item => item.value === this.form.type)
      }
    }
  },
  methods: {
    submit () {
      this.form.id || this.form.solid ? this.$emit('updateRecord') : this.$emit('addRecord')
    }
  }
}
</script>

<style>
.dns-record-modal-content {
  overflow: visible;
}

.dns-record-modal-dropdown {
  z-index: 1060 !important;
}
</style>
